// =============================================================================
// MeuDriver — Hook: useEarnings
// Repasses ao instrutor (instructor_earnings) e chave Pix de recebimento.
//
// O repasse nasce quando a aula é consumida (dada, aluno faltou ou cancelou com
// menos de 2 h) e fica disponível depois da janela pós-aula. É pago por Pix na
// chave cadastrada pelo instrutor — automático pela API de Payouts do Mercado
// Pago quando liberada, ou manualmente pelo admin ("Repasses a pagar").
// Migrations 20261011 e 20261011c.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

export type PixKeyType = 'cpf' | 'cnpj' | 'email' | 'phone' | 'random'

export const PIX_KEY_LABELS: Record<PixKeyType, string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  phone: 'Celular',
  random: 'Chave aleatória',
}

export interface PayoutAccount {
  instructor_id: string
  pix_key_type: PixKeyType
  pix_key: string
  holder_name: string
  updated_at: string
}

export type EarningStatus = 'pending' | 'processing' | 'paid' | 'reversed'

export interface Earning {
  id: string
  booking_id: string
  amount: number
  platform_amount: number
  reason: 'completed' | 'no_show' | 'late_cancel'
  status: EarningStatus
  available_at: string
  paid_at: string | null
  payout_method: 'mp_payout' | 'manual_pix' | null
  created_at: string
}

export const EARNING_REASON_LABELS: Record<Earning['reason'], string> = {
  completed: 'Aula dada',
  no_show: 'Aluno faltou',
  late_cancel: 'Cancelada em cima da hora',
}

/** id da linha em `instructors` do usuário logado. */
function useMyInstructorId() {
  const { user } = useAuth()
  return useQuery<string | null>({
    queryKey: ['my-instructor-id', user?.id],
    enabled: !!user?.id,
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await supabase.from('instructors').select('id').eq('profile_id', user!.id).maybeSingle()
      return (data as { id: string } | null)?.id ?? null
    },
  })
}

export function usePayoutAccount() {
  const { data: instructorId } = useMyInstructorId()
  return useQuery<PayoutAccount | null>({
    queryKey: ['payout-account', instructorId],
    enabled: !!instructorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_payout_accounts')
        .select('*')
        .eq('instructor_id', instructorId!)
        .maybeSingle()
      if (error) throw error
      return (data as PayoutAccount | null) ?? null
    },
  })
}

const PIX_ERRORS: Record<string, string> = {
  invalid_pix_key: 'Chave Pix inválida para o tipo escolhido.',
  invalid_holder: 'Informe o nome do titular da conta.',
  invalid_pix_key_type: 'Tipo de chave inválido.',
  not_an_instructor: 'Só instrutores cadastram chave de recebimento.',
}

export function useSavePixKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (p: { type: PixKeyType; key: string; holder: string }) => {
      const { data, error } = await supabase.rpc('set_instructor_pix_key', {
        p_type: p.type, p_key: p.key, p_holder: p.holder,
      })
      if (error) {
        const k = Object.keys(PIX_ERRORS).find((e) => error.message.includes(e))
        throw new Error(k ? PIX_ERRORS[k] : 'Não foi possível salvar a chave Pix.')
      }
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payout-account'] }),
  })
}

export function useMyEarnings() {
  const { data: instructorId } = useMyInstructorId()
  return useQuery<Earning[]>({
    queryKey: ['my-earnings', instructorId],
    enabled: !!instructorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_earnings')
        .select('id, booking_id, amount, platform_amount, reason, status, available_at, paid_at, payout_method, created_at')
        .eq('instructor_id', instructorId!)
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) throw error
      return ((data ?? []) as unknown as Earning[]).map((e) => ({
        ...e, amount: Number(e.amount), platform_amount: Number(e.platform_amount),
      }))
    },
  })
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface PayoutDue {
  instructor_id: string
  instructor_name: string | null
  pix_key_type: PixKeyType | null
  pix_key: string | null
  holder_name: string | null
  total: number
  lessons: number
  oldest: string
  earning_ids: string[]
  processing: number
}

export function useAdminPayoutsDue() {
  return useQuery<PayoutDue[]>({
    queryKey: ['admin-payouts-due'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_payouts_due')
      if (error) throw error
      return ((data ?? []) as PayoutDue[]).map((r) => ({
        ...r, total: Number(r.total), lessons: Number(r.lessons), processing: Number(r.processing),
      }))
    },
  })
}

export function useAdminMarkEarningsPaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (p: { earningIds: string[]; reference: string }) => {
      const { data, error } = await supabase.rpc('admin_mark_earnings_paid', {
        p_earning_ids: p.earningIds, p_reference: p.reference,
      })
      if (error) {
        throw new Error(error.message.includes('reference_required')
          ? 'Informe o identificador do Pix (ex.: código E2E do comprovante).'
          : 'Não foi possível marcar como pago.')
      }
      return Number(data ?? 0)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-payouts-due'] })
      qc.invalidateQueries({ queryKey: ['admin-financial-summary'] })
    },
  })
}
